import { TIDBDatabase, TIDBIndex, TIDBTable } from '@aksia/infrastructure';
import { Injectable } from '@angular/core';

// TODO: Unreferenced since phase 6b of the mock-data rebuild deleted
// IndexedDbTSQLService and the admin Data tooling (its only consumers).
// Kept per the phase-6b instruction; delete once confirmed dead.
@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  indexedDB: IDBFactory =
    window.indexedDB ||
    (window as any).mozIndexedDB ||
    (window as any).webkitIndexedDB ||
    (window as any).msIndexedDB;

  private openConnections: Map<string, IDBDatabase> = new Map();

  //#region ------------------------------ DATABASE ------------------------------

  public async listDatabases(): Promise<IDBDatabaseInfo[]> {
    if ('databases' in indexedDB) {
      // @ts-ignore - databases() is not yet in TypeScript's lib.dom.d.ts
      return indexedDB.databases();
    } else {
      throw new Error(
        'IndexedDB databases() method is not supported in this browser.',
      );
    }
  }

  public async connectDatabase(
    dbInfo: TIDBDatabase,
  ): Promise<IDBDatabase | undefined> {
    // Check if the connection is already open
    if (!dbInfo?.name) {
      return undefined;
    }
    if (this.openConnections.has(dbInfo.name)) {
      return this.openConnections.get(dbInfo.name)!;
    } else {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);

      return new Promise((resolve, reject) => {
        let existed = true;
        request.onsuccess = () => {
          const db: IDBDatabase = request.result;
          this.openConnections.set(dbInfo.name, db);
          resolve(db);
        };

        request.onerror = (event: any) => {
          reject(
            new Error(
              `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
            ),
          );
        };
        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
          existed = false;
        };
        request.onblocked = () => {
          console.warn(
            `Database ${dbInfo.name} upgrade blocked. Please close other connections.`,
          );
        };
      });
    }
  }

  public async createDatabase(
    dbInfo: TIDBDatabase,
    tablesInfo?: Array<TIDBTable>,
  ): Promise<IDBDatabase> {
    if (!dbInfo?.name) {
      throw new Error('Database name is required to create a database.');
    }
    return new Promise((resolve, reject) => {
      const request: IDBOpenDBRequest = window.indexedDB.open(
        dbInfo.name,
        dbInfo.version,
      );

      request.onerror = (event: any) => {
        // Handle database access errors (e.g., permissions, quota issues)
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };

      request.onblocked = () => {
        // Fires if another connection is preventing the upgrade [8, 9]
        console.warn(
          `Database ${dbInfo.name} upgrade blocked. Please close other connections.`,
        );
      };

      // This event handles schema creation and editing [10-12]
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db: IDBDatabase = request.result;

        // This transaction mode ("versionchange") is automatically created here [13]
        console.log(
          `Upgrading database ${dbInfo.name} from version ${event.oldVersion} to ${dbInfo.version}`,
        );

        // Structure Modification: Create/Edit Object Stores and Indexes

        for (const table of tablesInfo ?? []) {
          if (!db.objectStoreNames.contains(table.name)) {
            // Create Object Store: Only allowed within 'versionchange' transaction [10, 14]
            const store = db.createObjectStore(table.name, {
              keyPath: table.keyPath,
              autoIncrement: table.autoIncrement || false,
            });
            console.log(`Created Table: ${table.name}`);

            // Create Indexes (if configured)
            if (table.indices) {
              for (const indexConfig of table.indices) {
                // Indexes allow searching records using properties other than the primary key [15]
                store.createIndex(indexConfig.name, indexConfig.keyPath!, {
                  unique: indexConfig.unique || false,
                });
              }
            }
          } else {
            // Handle store updates/migrations if needed (e.g., index deletion/creation)
            const store = request.transaction!.objectStore(table.name);
            // Example: Check and add new indexes
            // Note: Deleting object stores uses IDBDatabase.deleteObjectStore(name) [16, 17]
          }
        }
      };

      request.onsuccess = () => {
        const db: IDBDatabase = request.result;
        // The IDBDatabase represents a connection to the database [18, 19]
        this.openConnections.set(dbInfo.name, db);

        // Listen for external version change requests to gracefully close (optional but recommended) [20]
        db.onversionchange = () => {
          db.close(); // Close the connection to allow a newer version to proceed [11, 21]
          this.openConnections.delete(dbInfo.name);
          console.warn(
            `Database ${dbInfo} connection closed due to external version change.`,
          );
        };

        resolve(db);
      };
    });
  }

  public async migrateDatabase(
    dbInfo: TIDBDatabase,
    newDbInfo: TIDBDatabase,
  ): Promise<IDBDatabase> {
    if (!dbInfo?.name || !newDbInfo?.name) {
      throw new Error('Both source and target database names are required.');
    }

    // 1. Connect to source and read everything
    const sourceDb = await this.connectDatabase(dbInfo);
    if (!sourceDb) {
      throw new Error(`Could not connect to source database ${dbInfo.name}`);
    }

    const schema: TIDBTable[] = [];
    const data = new Map<string, any[]>();
    const storeNames = Array.from(sourceDb.objectStoreNames);

    if (storeNames.length > 0) {
      const transaction = sourceDb.transaction(storeNames, 'readonly');

      const readPromises = storeNames.map((storeName) => {
        const store = transaction.objectStore(storeName);

        // Extract Schema
        const indices: TIDBIndex[] = [];
        for (const indexName of Array.from(store.indexNames)) {
          const index = store.index(indexName);
          indices.push({
            name: index.name,
            keyPath: index.keyPath as string | string[],
            unique: index.unique,
            multiEntry: index.multiEntry,
          } as any);
        }

        schema.push({
          name: store.name,
          keyPath: store.keyPath as string | string[],
          autoIncrement: store.autoIncrement,
          indices: indices,
          columns: [],
        } as any);

        // Extract Data
        return new Promise((resolve, reject) => {
          const request = store.getAll();
          request.onsuccess = () => {
            data.set(storeName, request.result);
            resolve(request.result);
          };
          request.onerror = () => reject(request.error);
        });
      });

      await Promise.all(readPromises);
    }

    sourceDb.close();
    this.openConnections.delete(dbInfo.name);

    // 2. Create target database with extracted schema
    const targetDb = await this.createDatabase(newDbInfo, schema);

    // 3. Write data to target
    if (data.size > 0) {
      const targetStoreNames = Array.from(data.keys());
      const validStoreNames = targetStoreNames.filter((name) =>
        targetDb.objectStoreNames.contains(name),
      );

      if (validStoreNames.length > 0) {
        const transaction = targetDb.transaction(validStoreNames, 'readwrite');

        for (const storeName of validStoreNames) {
          const store = transaction.objectStore(storeName);
          const records = data.get(storeName);
          if (records) {
            for (const record of records) {
              store.add(record);
            }
          }
        }

        await new Promise<IDBDatabase>((resolve, reject) => {
          transaction.oncomplete = () => resolve(targetDb);
          transaction.onabort = () => reject(transaction.error);
          transaction.onerror = () => reject(transaction.error);
        });
      }
    }

    console.log(`Migrated database from ${dbInfo.name} to ${newDbInfo.name}`);
    return targetDb;
  }

  public async deleteDatabase(dbName: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      // Close connection if open, to avoid blocking the deletion [11, 36]
      if (this.openConnections.has(dbName)) {
        this.openConnections.get(dbName)!.close();
        this.openConnections.delete(dbName);
      }

      // Use IDBFactory.deleteDatabase(name) [37]
      const request = window.indexedDB.deleteDatabase(dbName);

      request.onblocked = () => {
        // Fires if deletion is blocked by other open connections [38]
        reject(new Error(`Deletion of ${dbName} blocked by other clients.`));
      };

      request.onerror = (event: any) => {
        reject(request.error);
      };

      request.onsuccess = () => {
        // Result will be null upon successful deletion [37, 39]
        const upgradedDb: IDBDatabase = request.result;
        console.log(`Database ${dbName} successfully deleted.`);
        resolve(upgradedDb);
      };
    });
  }

  //#endregion -------------------------------------------------------------------

  //#region ------------------------------ TABLE ---------------------------------

  public async listTables(dbInfo: TIDBDatabase): Promise<IDBObjectStore[]> {
    if (!dbInfo?.name) {
      return [];
    }
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      return [];
    }
    return Array.from(db.objectStoreNames).map((storeName) =>
      db.transaction(storeName, 'readonly').objectStore(storeName),
    );
  }

  public async createTable(
    dbInfo: TIDBDatabase,
    tableInfo: TIDBTable,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase({ name: dbInfo.name });
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (db.objectStoreNames.contains(tableInfo.name)) {
      throw new Error(
        `Table ${tableInfo.name} already exists in ${dbInfo.name}.`,
      );
    }
    const currentVersion = db.version;
    db.close();
    this.openConnections.delete(dbInfo.name);

    const targetVersion =
      dbInfo.version && dbInfo.version > currentVersion
        ? dbInfo.version
        : currentVersion + 1;

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, targetVersion);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (!upgradedDb.objectStoreNames.contains(tableInfo.name)) {
          const store = upgradedDb.createObjectStore(tableInfo.name, {
            keyPath: tableInfo.keyPath,
            autoIncrement: tableInfo.autoIncrement || false,
          });
          console.log(`Created Table: ${tableInfo.name}`);
          // Create Indexes (if configured)
          if (tableInfo.indices) {
            for (const indexConfig of tableInfo.indices) {
              store.createIndex(indexConfig.name, indexConfig.keyPath!, {
                unique: indexConfig.unique || false,
              });
            }
          }
        } else {
          // Should not happen if check above passed, unless race condition or logic error
          // But strict mode might complain.
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  public async updateTable(
    dbInfo: TIDBDatabase,
    tableInfo: TIDBTable,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (!db.objectStoreNames.contains(tableInfo.name)) {
      throw new Error(
        `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
      );
    }
    db.close();
    this.openConnections.delete(dbInfo.name);
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (upgradedDb.objectStoreNames.contains(tableInfo.name)) {
          // Note: IndexedDB does not support direct modification of object store properties.
          // To change keyPath or autoIncrement, the store must be deleted and recreated,
          upgradedDb.deleteObjectStore(tableInfo.name);
          const store = upgradedDb.createObjectStore(tableInfo.name, {
            keyPath: tableInfo.keyPath,
            autoIncrement: tableInfo.autoIncrement || false,
          });
          console.log(`Updated Table: ${tableInfo.name}`);
          // Recreate Indexes (if configured)
          if (tableInfo.indices) {
            for (const indexConfig of tableInfo.indices) {
              store.createIndex(indexConfig.name, indexConfig.keyPath!, {
                unique: indexConfig.unique || false,
              });
            }
          }
        } else {
          reject(
            new Error(
              `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
            ),
          );
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name!, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  public async deleteTable(
    dbInfo: TIDBDatabase,
    tableName: string,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (!db.objectStoreNames.contains(tableName)) {
      throw new Error(`Table ${tableName} does not exist in ${dbInfo.name}.`);
    }
    db.close();
    this.openConnections.delete(dbInfo.name);
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (upgradedDb.objectStoreNames.contains(tableName)) {
          upgradedDb.deleteObjectStore(tableName);
          console.log(`Deleted Table: ${tableName}`);
        } else {
          reject(
            new Error(`Table ${tableName} does not exist in ${dbInfo.name}.`),
          );
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  //#endregion -------------------------------------------------------------------

  //#region ------------------------------ INDEX ---------------------------------

  public async listIndices(
    dbInfo?: TIDBDatabase,
    tableName?: string,
  ): Promise<IDBIndex[]> {
    if (!dbInfo || !tableName) {
      return [];
    }
    const db = await this.connectDatabase(dbInfo);
    if (!db!.objectStoreNames.contains(tableName)) {
      return [];
    }
    const transaction = db!.transaction(tableName, 'readonly');
    const store = transaction.objectStore(tableName);
    return Array.from(store.indexNames).map((indexName) =>
      store.index(indexName),
    );
  }

  public async createIndex(
    dbInfo: TIDBDatabase,
    tableInfo: TIDBTable,
    indexInfo: TIDBIndex,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (!db.objectStoreNames.contains(tableInfo.name)) {
      throw new Error(
        `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
      );
    }
    const store = db
      .transaction(tableInfo.name, 'readonly')
      .objectStore(tableInfo.name);
    if (store.indexNames.contains(indexInfo.name)) {
      throw new Error(
        `Index ${indexInfo.name} already exists in table ${tableInfo.name}.`,
      );
    }
    db.close();
    this.openConnections.delete(dbInfo.name);
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (upgradedDb.objectStoreNames.contains(tableInfo.name)) {
          const store = request.transaction!.objectStore(tableInfo.name);
          store.createIndex(indexInfo.name, indexInfo.keyPath!, {
            unique: indexInfo.unique || false,
          });
          console.log(
            `Created Index: ${indexInfo.name} in Table: ${tableInfo.name}`,
          );
        } else {
          reject(
            new Error(
              `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
            ),
          );
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  public async updateIndex(
    dbInfo: TIDBDatabase,
    tableInfo: TIDBTable,
    indexInfo: TIDBIndex,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (!db.objectStoreNames.contains(tableInfo.name)) {
      throw new Error(
        `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
      );
    }
    const store = db
      .transaction(tableInfo.name, 'readonly')
      .objectStore(tableInfo.name);
    if (!store.indexNames.contains(indexInfo.name)) {
      throw new Error(
        `Index ${indexInfo.name} does not exist in table ${tableInfo.name}.`,
      );
    }
    db.close();
    this.openConnections.delete(dbInfo.name);
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (upgradedDb.objectStoreNames.contains(tableInfo.name)) {
          const store = request.transaction!.objectStore(tableInfo.name);
          store.deleteIndex(indexInfo.name);
          store.createIndex(indexInfo.name, indexInfo.keyPath!, {
            unique: indexInfo.unique || false,
          });
          console.log(
            `Updated Index: ${indexInfo.name} in Table: ${tableInfo.name}`,
          );
        } else {
          reject(
            new Error(
              `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
            ),
          );
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  public async deleteIndex(
    dbInfo: TIDBDatabase,
    tableInfo: TIDBTable,
    indexName: string,
  ): Promise<IDBDatabase> {
    const db = await this.connectDatabase(dbInfo);
    if (!db) {
      throw new Error(`Database ${dbInfo.name} not connected.`);
    }
    if (!db.objectStoreNames.contains(tableInfo.name)) {
      throw new Error(
        `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
      );
    }
    const store = db
      .transaction(tableInfo.name, 'readonly')
      .objectStore(tableInfo.name);
    if (!store.indexNames.contains(indexName)) {
      throw new Error(
        `Index ${indexName} does not exist in table ${tableInfo.name}.`,
      );
    }
    db.close();
    this.openConnections.delete(dbInfo.name);
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(dbInfo.name, dbInfo.version);
      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const upgradedDb: IDBDatabase = request.result;
        if (upgradedDb.objectStoreNames.contains(tableInfo.name)) {
          const store = request.transaction!.objectStore(tableInfo.name);
          store.deleteIndex(indexName);
          console.log(
            `Deleted Index: ${indexName} from Table: ${tableInfo.name}`,
          );
        } else {
          reject(
            new Error(
              `Table ${tableInfo.name} does not exist in ${dbInfo.name}.`,
            ),
          );
        }
      };
      request.onsuccess = () => {
        const upgradedDb: IDBDatabase = request.result;
        this.openConnections.set(dbInfo.name!, upgradedDb);
        resolve(upgradedDb);
      };
      request.onerror = (event: any) => {
        reject(
          new Error(
            `IndexedDB Error for ${dbInfo.name}: ${request.error?.name}`,
          ),
        );
      };
    });
  }

  //#endregion -------------------------------------------------------------------

  //#region ------------------------------ COLUMNS -------------------------------

  public async listColumns(
    dbInfo?: TIDBDatabase,
    tableName?: string,
  ): Promise<Array<{ name: string; dataType?: string }>> {
    if (!dbInfo || !tableName) {
      return [];
    }
    const db = await this.connectDatabase(dbInfo);
    if (!db!.objectStoreNames.contains(tableName)) {
      return [];
    }
    const transaction = db!.transaction(tableName, 'readonly');
    const store = transaction.objectStore(tableName);
    const request = store.openCursor();
    return new Promise<Array<{ name: string; dataType?: string }>>(
      (resolve, reject) => {
        request.onsuccess = (event: any) => {
          const cursor: IDBCursorWithValue = event.target.result;
          if (cursor) {
            const firstRecord = cursor.value;
            const columns = Object.entries(firstRecord)?.map(
              ([key, value]) => ({
                name: key,
                dataType:
                  value === undefined || value === null
                    ? undefined
                    : (typeof value).toString(),
              }),
            );
            resolve(columns);
          } else {
            resolve([]);
          }
        };
        request.onerror = (event: any) => {
          reject(request.error);
        };
      },
    );
  }

  //#endregion -------------------------------------------------------------------

  // --- TRANSACTIONS ---

  /**
   * Runs a transactional operation on an object store.
   * @param dbName The database name.
   * @param storeName The object store to access (scope of the transaction) [22].
   * @param mode 'readonly' or 'readwrite' [23].
   * @param operation The function containing the actual IndexedDB request.
   */
  private async runTransaction<T>(
    dbName: string,
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest,
  ): Promise<T> {
    const db = this.openConnections.get(dbName);
    if (!db) {
      throw new Error(`Database ${dbName} not connected.`);
    }

    return new Promise((resolve, reject) => {
      // Create transaction specifying scope and mode [18, 22, 24]
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);

      // Perform the specific data operation
      const request = operation(store);

      request.onsuccess = (event: any) => {
        // When the request succeeds, resolve the result [25]
        resolve(request.result as T);
      };

      request.onerror = (event: any) => {
        // If the individual request fails, the transaction aborts automatically unless prevented [26, 27]
        reject(request.error);
      };

      transaction.oncomplete = () => {
        // Transaction committed successfully [28]
      };

      transaction.onabort = (event: any) => {
        // Transaction aborted (e.g., due to an error, like a ConstraintError [29])
        reject(transaction.error);
      };
    });
  }

  // --- 2. CREATE RECORDS ---

  /**
   * Creates a new record in a specific object store.
   * Uses IDBObjectStore.add() which checks for key uniqueness [30].
   * @param dbName The database name.
   * @param storeName The object store name.
   * @param value The data object to store.
   * @param key Optional explicit key for out-of-line stores [31].
   */
  public async createRecord<T>(
    dbName: string,
    storeName: string,
    value: T,
    key?: IDBValidKey,
  ): Promise<IDBValidKey> {
    return this.runTransaction<IDBValidKey>(
      dbName,
      storeName,
      'readwrite',
      (store) => store.add(value, key), // add(value, key) adds the record [31]
    );
  }

  // --- 3. EDIT (UPDATE) RECORDS ---

  /**
   * Updates an existing record or creates a new one if the key does not exist.
   * Uses IDBObjectStore.put() [30].
   * @param dbName The database name.
   * @param storeName The object store name.
   * @param value The data object to store.
   * @param key Optional explicit key for out-of-line stores [31].
   */
  public async editRecord<T>(
    dbName: string,
    storeName: string,
    value: T,
    key?: IDBValidKey,
  ): Promise<IDBValidKey> {
    return this.runTransaction<IDBValidKey>(
      dbName,
      storeName,
      'readwrite',
      (store) => store.put(value, key), // put(value, key) updates or adds the record [31, 32]
    );
  }

  // --- 4. OPEN (READ) RECORDS ---

  /**
   * Reads a single record by its primary key or key range.
   * @param dbName The database name.
   * @param storeName The object store name.
   * @param keyOrRange The key or IDBKeyRange to query [33].
   */
  public async readRecord<T>(
    dbName: string,
    storeName: string,
    keyOrRange: IDBValidKey | IDBKeyRange,
  ): Promise<T | undefined> {
    return this.runTransaction<T | undefined>(
      dbName,
      storeName,
      'readonly',
      (store) => store.get(keyOrRange), // get(query) retrieves the value of the first matching record [33]
    );
  }

  /**
   * Reads multiple records.
   * @param dbName The database name.
   * @param storeName The object store name.
   * @param query The key, key range, or IDBGetAllOptions [34].
   */
  public async readAllRecords<T>(
    dbName: string,
    storeName: string,
    query?: IDBValidKey | IDBKeyRange,
    count?: number,
  ): Promise<T[]> {
    return this.runTransaction<T[]>(
      dbName,
      storeName,
      'readonly',
      (store) => store.getAll(query, count), // getAll retrieves multiple values [34]
    );
  }

  // --- 5. DELETE RECORDS ---

  /**
   * Deletes a record or a range of records.
   * @param dbName The database name.
   * @param storeName The object store name.
   * @param query The key or key range of records to delete [35].
   */
  public async deleteRecord(
    dbName: string,
    storeName: string,
    query: IDBValidKey | IDBKeyRange,
  ): Promise<void> {
    return this.runTransaction<void>(
      dbName,
      storeName,
      'readwrite',
      (store) => store.delete(query), // delete(query) deletes matching records [30, 35]
    );
  }

  // --- 6. DELETE MULTIPLE DATABASES ---
}

/* interface DbStore {
  name: string;
  keyPath: string;
  autoIncrement?: boolean;
  indexes?: { name: string; keyPath: string; unique?: boolean }[];
} */

interface DatabaseConfiguration {
  version: number;
  stores?: TIDBTable[];
}

interface IDBGetAllOptions {
  query?: any;
  count?: number;
  direction?: IDBCursorDirection;
}
